// Builds the request's AbortSignal: the default timeout combined with any
// caller-provided signal.
//
// Why combine (not replace): services pass their own `signal` via
// `RequestConfigType` (component unmount, manual cancel, superseded request).
// Overwriting it with the timeout signal would silently drop their
// cancellation. We merge both so whichever fires first aborts the request.
//
// Defensive: degrades gracefully if the runtime lacks `AbortSignal.timeout`
// (broad support) or `AbortSignal.any` (newer) so an older browser never throws
// here — it just keeps the previous (un-timed-out) behaviour.
export function buildRequestSignal(
    callerSignal: AbortSignal | null | undefined,
    timeoutMs: number,
): AbortSignal | null | undefined {
    if (typeof AbortSignal === "undefined" || typeof AbortSignal.timeout !== "function") {
        return callerSignal;
    }

    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    if (!callerSignal) return timeoutSignal;

    if (typeof AbortSignal.any === "function") {
        return AbortSignal.any([timeoutSignal, callerSignal]);
    }

    // No `AbortSignal.any`: merge manually so BOTH the timeout and the caller's
    // cancellation are honoured. Whichever aborts first aborts the controller,
    // forwarding the original abort reason; listeners are cleared on first abort.
    const controller = new AbortController();
    const sources = [timeoutSignal, callerSignal];

    function onAbort(event: Event) {
        controller.abort((event.target as AbortSignal | null)?.reason);
        removeListeners();
    }

    function removeListeners() {
        for (const source of sources) {
            source.removeEventListener("abort", onAbort);
        }
    }

    for (const source of sources) {
        if (source.aborted) {
            controller.abort(source.reason);
            removeListeners();
            return controller.signal;
        }
        source.addEventListener("abort", onAbort);
    }

    return controller.signal;
}
